/**
 * Field Configuration Modal
 * Popup for editing field properties inline
 */

import React, { useState, useEffect } from 'react';
import { X, Settings, Sparkles } from 'lucide-react';
import type { FieldSchema, FieldType } from '../../types/formSchema';

interface FieldConfigModalProps {
  field: FieldSchema | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (field: FieldSchema) => void;
  onDelete?: () => void;
  existingSections?: string[]; // List of existing section names for dropdown
}

const FIELD_TYPES: Array<{ value: FieldType; label: string; icon: string; description: string }> = [
  { value: 'text', label: 'Text', icon: '📝', description: 'Single line text input' },
  { value: 'textarea', label: 'Long Text', icon: '📄', description: 'Multi-line text area' },
  { value: 'number', label: 'Number', icon: '🔢', description: 'Numeric input with units' },
  { value: 'date', label: 'Date', icon: '📅', description: 'Date picker' },
  { value: 'dropdown', label: 'Dropdown', icon: '📋', description: 'Select from list' },
  { value: 'radio', label: 'Radio Buttons', icon: '🔘', description: 'Single choice' },
  { value: 'checkbox', label: 'Checkboxes', icon: '☑️', description: 'Multiple choice' },
  { value: 'boolean', label: 'Yes/No', icon: '✓', description: 'Boolean toggle' },
  { value: 'image', label: 'Photo Capture', icon: '📷', description: 'Multiple photos with notes' },
  { value: 'signature', label: 'Signature', icon: '✍️', description: 'Drawn signature' },
];

export default function FieldConfigModal({
  field,
  isOpen,
  onClose,
  onSave,
  onDelete,
  existingSections = [],
}: FieldConfigModalProps) {
  const [editedField, setEditedField] = useState<FieldSchema | null>(null);
  const [isCreatingNewSection, setIsCreatingNewSection] = useState(false);
  const [customSectionName, setCustomSectionName] = useState('');

  // Debug: Log section creation state changes
  useEffect(() => {
    console.log('[FieldConfigModal] Section creation state:', {
      isCreatingNewSection,
      customSectionName,
      currentSection: editedField ? (editedField as any).section : undefined,
      buttonDisabled: !customSectionName.trim()
    });
  }, [isCreatingNewSection, customSectionName, editedField]);

  useEffect(() => {
    if (isOpen) {
      console.log('[FieldConfigModal] Modal opened, resetting section creation state');
      setIsCreatingNewSection(false);
      setCustomSectionName('');
      
      if (field) {
        // Editing existing field
        console.log('[FieldConfigModal] Editing existing field:', field);
        setEditedField({ ...field });
      } else {
        // Creating new field - set defaults
        console.log('[FieldConfigModal] Creating new field with defaults');
        setEditedField({
          id: `custom_${Date.now()}`,
          type: 'text',
          label: '',
          required: false,
          order: 0,
        } as FieldSchema);
      }
    }
  }, [field, isOpen]);

  // Include the newly created section in the dropdown list
  // IMPORTANT: This must be before the early return to maintain hook order
  const allSections = React.useMemo(() => {
    const sections = new Set(existingSections);
    
    // Add the current field's section if it's not already in the list
    if (editedField && (editedField as any).section) {
      sections.add((editedField as any).section);
    }
    
    return Array.from(sections).sort();
  }, [existingSections, editedField]);

  if (!isOpen || !editedField) return null;

  const handleSave = () => {
    if (editedField) {
      onSave(editedField);
      onClose();
    }
  };

  const handleTypeChange = (type: FieldType) => {
    setEditedField({
      ...editedField,
      type,
      // Reset type-specific properties
      options: (type === 'dropdown' || type === 'multiChoice' || type === 'radio' || type === 'checkbox') ? [] : undefined,
      allowMultiple: type === 'multiChoice' ? false : undefined,
      displayStyle: type === 'boolean' ? 'yes-no' : (type === 'radio' || type === 'checkbox' ? 'list' : undefined),
      displayMode: type === 'image' ? 'cdn' : undefined,
      maxImages: type === 'image' ? 10 : undefined,
      unit: type === 'number' ? '' : undefined,
      min: type === 'number' ? undefined : undefined,
      max: type === 'number' ? undefined : undefined,
      step: type === 'number' ? 1 : undefined,
      rows: type === 'textarea' ? 4 : undefined,
      maxLength: type === 'text' || type === 'textarea' ? undefined : undefined,
      width: type === 'signature' ? 400 : undefined,
      height: type === 'signature' ? 200 : undefined,
    } as FieldSchema);
  };

  const addOption = () => {
    const hasOptions = editedField.type === 'dropdown' || editedField.type === 'multiChoice' || 
                       editedField.type === 'radio' || editedField.type === 'checkbox';
    const options = hasOptions ? (editedField as any).options || [] : [];
    
    setEditedField({
      ...editedField,
      options: [
        ...options,
        { value: `option${options.length + 1}`, label: `Option ${options.length + 1}` }
      ]
    } as FieldSchema);
  };

  const updateOption = (index: number, key: 'value' | 'label', newValue: string) => {
    const hasOptions = editedField.type === 'dropdown' || editedField.type === 'multiChoice' || 
                       editedField.type === 'radio' || editedField.type === 'checkbox';
    const options = hasOptions ? (editedField as any).options || [] : [];
    
    const updated = [...options];
    updated[index] = { ...updated[index], [key]: newValue };
    
    setEditedField({
      ...editedField,
      options: updated
    } as FieldSchema);
  };

  const removeOption = (index: number) => {
    const hasOptions = editedField.type === 'dropdown' || editedField.type === 'multiChoice' || 
                       editedField.type === 'radio' || editedField.type === 'checkbox';
    const options = hasOptions ? (editedField as any).options || [] : [];
    
    setEditedField({
      ...editedField,
      options: options.filter((_: any, i: number) => i !== index)
    } as FieldSchema);
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative z-[10000] border border-white/20">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-6 py-5 flex items-center justify-between z-10 rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 rounded-full p-2">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Configure Field</h2>
              <p className="text-blue-100 text-sm">Customize your form field</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 bg-gradient-to-b from-gray-50/50 to-white">
          {/* Field Type */}
          <div>
            <label className="flex items-center text-sm font-semibold text-gray-900 mb-3">
              <Sparkles className="w-4 h-4 text-blue-500 mr-2" />
              Field Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {FIELD_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleTypeChange(type.value)}
                  className={`p-3 border-2 rounded-xl text-left transition-all duration-200 ${
                    editedField.type === type.value
                      ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 ring-2 ring-blue-200 shadow-md transform scale-[1.02]'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-sm bg-white'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-2xl">{type.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-900">{type.label}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{type.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              <span className="text-red-500 mr-1">*</span>
              Field Label
            </label>
            <input
              type="text"
              value={editedField.label}
              onChange={(e) => setEditedField({ ...editedField, label: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-white/50 backdrop-blur-sm"
              placeholder="e.g., Property Address"
            />
          </div>

          {/* Placeholder (text fields only) */}
          {(editedField.type === 'text' || editedField.type === 'textarea') && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Placeholder
              </label>
              <input
                type="text"
                value={editedField.placeholder || ''}
                onChange={(e) => setEditedField({ ...editedField, placeholder: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-white/50"
                placeholder="e.g., Enter property address"
              />
            </div>
          )}

          {/* Help Text */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Help Text
            </label>
            <input
              type="text"
              value={editedField.helpText || ''}
              onChange={(e) => setEditedField({ ...editedField, helpText: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-white/50"
              placeholder="e.g., Include postcode"
            />
          </div>

          {/* Required Checkbox */}
          <div className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-xl">
            <input
              type="checkbox"
              id="required"
              checked={editedField.required || false}
              onChange={(e) => setEditedField({ ...editedField, required: e.target.checked })}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded-md focus:ring-blue-500"
            />
            <label htmlFor="required" className="text-sm font-medium text-gray-900">
              Required field
            </label>
          </div>

          {/* Section Organization */}
          <div className="space-y-4 p-4 bg-indigo-50/50 border border-indigo-100 rounded-xl">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">📂 Section Organization</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Name
                </label>
                {!isCreatingNewSection ? (
                  <div className="space-y-2">
                    <select
                      value={(editedField as any).section || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '__CREATE_NEW__') {
                          setIsCreatingNewSection(true);
                          setCustomSectionName('');
                          setEditedField({ ...editedField, section: undefined } as FieldSchema);
                        } else {
                          setEditedField({ ...editedField, section: value || undefined } as FieldSchema);
                        }
                      }}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all bg-white"
                    >
                      <option value="">No Section (Default)</option>
                      {allSections.map(section => (
                        <option key={section} value={section}>
                          {section}
                        </option>
                      ))}
                      <option value="__CREATE_NEW__" className="font-semibold text-indigo-600">
                        ➕ Create New Section...
                      </option>
                    </select>
                    <p className="text-xs text-gray-500">Choose existing or create new</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customSectionName}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          console.log('[FieldConfigModal] Section name input changed:', newValue);
                          setCustomSectionName(newValue);
                        }}
                        onKeyDown={(e) => {
                          console.log('[FieldConfigModal] Key pressed:', e.key, 'Current value:', customSectionName);
                          
                          if (e.key === 'Enter' && customSectionName.trim()) {
                            console.log('[FieldConfigModal] Enter key - creating section:', customSectionName.trim());
                            setEditedField({ ...editedField, section: customSectionName.trim() } as FieldSchema);
                            setIsCreatingNewSection(false);
                            setCustomSectionName('');
                          } else if (e.key === 'Escape') {
                            console.log('[FieldConfigModal] Escape key - canceling section creation');
                            setIsCreatingNewSection(false);
                            setCustomSectionName('');
                          }
                        }}
                        placeholder="Enter new section name"
                        className="flex-1 px-3 py-2 border-2 border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all bg-white"
                        autoFocus
                      />
                      <button
                        onClick={() => {
                          console.log('[FieldConfigModal] Confirm new section clicked:', {
                            customSectionName,
                            trimmed: customSectionName.trim(),
                            currentSection: (editedField as any).section,
                            editedField
                          });
                          
                          if (customSectionName.trim()) {
                            const newSectionName = customSectionName.trim();
                            console.log('[FieldConfigModal] Setting section to:', newSectionName);
                            
                            setEditedField({ ...editedField, section: newSectionName } as FieldSchema);
                            setIsCreatingNewSection(false);
                            setCustomSectionName('');
                            
                            console.log('[FieldConfigModal] Section creation complete');
                          } else {
                            console.warn('[FieldConfigModal] Section name is empty after trim');
                          }
                        }}
                        disabled={!customSectionName.trim()}
                        className="px-3 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => {
                          setIsCreatingNewSection(false);
                          setCustomSectionName('');
                        }}
                        className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                      >
                        ✕
                      </button>
                    </div>
                    <p className="text-xs text-indigo-600">Press Enter to confirm, Escape to cancel</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order in Section
                </label>
                <input
                  type="number"
                  value={(editedField as any).sectionOrder ?? ''}
                  onChange={(e) => setEditedField({ ...editedField, sectionOrder: e.target.value ? Number(e.target.value) : undefined } as FieldSchema)}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all bg-white"
                  placeholder="1"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Field position within section</p>
              </div>
            </div>
            <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
              <p className="text-xs text-gray-700">
                📋 Group related fields together by selecting the same section. New sections are automatically available for other fields.
              </p>
            </div>
          </div>

          {/* Number-specific options */}
          {editedField.type === 'number' && (
            <div className="space-y-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Number Options</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Value
                  </label>
                  <input
                    type="number"
                    value={editedField.min ?? ''}
                    onChange={(e) => setEditedField({ ...editedField, min: e.target.value ? Number(e.target.value) : undefined } as FieldSchema)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-white"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Value
                  </label>
                  <input
                    type="number"
                    value={editedField.max ?? ''}
                    onChange={(e) => setEditedField({ ...editedField, max: e.target.value ? Number(e.target.value) : undefined } as FieldSchema)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-white"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={editedField.unit || ''}
                    onChange={(e) => setEditedField({ ...editedField, unit: e.target.value } as FieldSchema)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-white"
                    placeholder="m², °C"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Text-specific options */}
          {(editedField.type === 'text' || editedField.type === 'textarea') && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Max Length
              </label>
              <input
                type="number"
                value={editedField.maxLength ?? ''}
                onChange={(e) => setEditedField({ ...editedField, maxLength: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-white/50"
                placeholder="e.g., 500"
              />
            </div>
          )}

          {/* Textarea rows */}
          {editedField.type === 'textarea' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Rows
              </label>
              <input
                type="number"
                value={editedField.rows || 4}
                onChange={(e) => setEditedField({ ...editedField, rows: Number(e.target.value) } as FieldSchema)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-white/50"
                min="1"
                max="20"
              />
            </div>
          )}

          {/* Options (dropdown/multiChoice/radio/checkbox) */}
          {(editedField.type === 'dropdown' || editedField.type === 'multiChoice' || editedField.type === 'radio' || editedField.type === 'checkbox') && (
            <div className="p-4 bg-indigo-50/50 border border-indigo-100 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-900">
                  Options {editedField.type === 'radio' && <span className="text-xs text-gray-600">(single choice)</span>}
                  {editedField.type === 'checkbox' && <span className="text-xs text-gray-600">(multiple choice)</span>}
                </label>
                <button
                  onClick={addOption}
                  className="text-sm text-blue-600 hover:text-blue-700 font-semibold px-3 py-1.5 bg-white rounded-lg border border-blue-200 hover:border-blue-300 transition-all"
                >
                  + Add Option
                </button>
              </div>
              <div className="space-y-2">
                {((editedField as any).options || []).map((option: any, index: number) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => updateOption(index, 'label', e.target.value)}
                      className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-white"
                      placeholder="Option label"
                    />
                    <button
                      onClick={() => removeOption(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-200 hover:border-red-300 transition-all font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Multiple choice specific */}
          {editedField.type === 'multiChoice' && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allowMultiple"
                checked={editedField.allowMultiple || false}
                onChange={(e) => setEditedField({ ...editedField, allowMultiple: e.target.checked } as FieldSchema)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="allowMultiple" className="text-sm font-medium text-gray-700">
                Allow multiple selections
              </label>
            </div>
          )}

          {/* Radio/Checkbox display style */}
          {(editedField.type === 'radio' || editedField.type === 'checkbox') && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Display Style
              </label>
              <select
                value={(editedField as any).displayStyle || 'list'}
                onChange={(e) => setEditedField({ ...editedField, displayStyle: e.target.value as any } as FieldSchema)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-white/50"
              >
                <option value="list">Vertical List</option>
                <option value="inline">Horizontal Inline</option>
                <option value="grid">Grid (2 columns)</option>
              </select>
            </div>
          )}

          {/* Boolean display style */}
          {editedField.type === 'boolean' && (
            <div className="space-y-4 p-4 bg-yellow-50/50 border border-yellow-100 rounded-xl">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Boolean Options</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Style
                </label>
                <select
                  value={editedField.displayStyle || 'yes-no'}
                  onChange={(e) => setEditedField({ ...editedField, displayStyle: e.target.value as any } as FieldSchema)}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all bg-white"
                >
                  <option value="yes-no">Yes / No</option>
                  <option value="yes-no-na">Yes / No / N/A</option>
                  <option value="toggle">Toggle Switch</option>
                </select>
              </div>
              
              {/* Photo Capture Toggle */}
              <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="enablePhotoCapture"
                  checked={(editedField as any).enablePhotoCapture || false}
                  onChange={(e) => setEditedField({ 
                    ...editedField, 
                    enablePhotoCapture: e.target.checked,
                    photoTrigger: e.target.checked ? 'always' : undefined,
                    maxPhotos: e.target.checked ? 10 : undefined
                  } as FieldSchema)}
                  className="w-5 h-5 text-yellow-500 rounded focus:ring-2 focus:ring-yellow-400"
                />
                <label htmlFor="enablePhotoCapture" className="text-sm font-medium text-gray-900 cursor-pointer">
                  Enable Photo Capture
                </label>
              </div>
              
              {/* Photo Capture Options (shown only when enabled) */}
              {(editedField as any).enablePhotoCapture && (
                <div className="space-y-4 pl-4 border-l-2 border-yellow-300">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Show Photos When
                    </label>
                    <select
                      value={(editedField as any).photoTrigger || 'always'}
                      onChange={(e) => setEditedField({ ...editedField, photoTrigger: e.target.value as any } as FieldSchema)}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all bg-white"
                    >
                      <option value="always">Always (Show for any selection)</option>
                      <option value="yes">Only when "Yes" is selected</option>
                      <option value="no">Only when "No" is selected</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Photos
                    </label>
                    <input
                      type="number"
                      value={(editedField as any).maxPhotos || 10}
                      onChange={(e) => setEditedField({ ...editedField, maxPhotos: Number(e.target.value) } as FieldSchema)}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all bg-white"
                      min="1"
                      max="50"
                    />
                  </div>
                  
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <p className="text-xs text-gray-700">
                      📷 Users will see photo capture options based on their selection.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Photo Capture Options (for all field types except image, signature, and boolean) */}
          {['text', 'textarea', 'number', 'date', 'dropdown', 'radio', 'checkbox'].includes(editedField.type) && (
            <div className="space-y-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">📸 Photo Attachment</h4>
              
              {/* Photo Capture Toggle */}
              <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="enablePhotoCapture"
                  checked={(editedField as any).enablePhotoCapture || false}
                  onChange={(e) => setEditedField({ 
                    ...editedField, 
                    enablePhotoCapture: e.target.checked,
                    photoTrigger: e.target.checked ? 'always' : undefined,
                    maxPhotos: e.target.checked ? 10 : undefined
                  } as FieldSchema)}
                  className="w-5 h-5 text-blue-500 rounded focus:ring-2 focus:ring-blue-400"
                />
                <label htmlFor="enablePhotoCapture" className="text-sm font-medium text-gray-900 cursor-pointer">
                  Enable Photo Attachment
                </label>
              </div>
              
              {/* Photo Capture Options (shown only when enabled) */}
              {(editedField as any).enablePhotoCapture && (
                <div className="space-y-4 pl-4 border-l-2 border-blue-300">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Photos
                    </label>
                    <input
                      type="number"
                      value={(editedField as any).maxPhotos || 10}
                      onChange={(e) => setEditedField({ ...editedField, maxPhotos: Number(e.target.value) } as FieldSchema)}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all bg-white"
                      min="1"
                      max="50"
                    />
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-xs text-gray-700">
                      📷 Users can attach photos to this field for documentation purposes.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Signature options */}
          {editedField.type === 'signature' && (
            <div className="space-y-4 p-4 bg-purple-50/50 border border-purple-100 rounded-xl">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Signature Canvas</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Canvas Width (px)
                  </label>
                  <input
                    type="number"
                    value={(editedField as any).width || 400}
                    onChange={(e) => setEditedField({ ...editedField, width: Number(e.target.value) } as FieldSchema)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all bg-white"
                    min="200"
                    max="800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Canvas Height (px)
                  </label>
                  <input
                    type="number"
                    value={(editedField as any).height || 200}
                    onChange={(e) => setEditedField({ ...editedField, height: Number(e.target.value) } as FieldSchema)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all bg-white"
                    min="100"
                    max="400"
                  />
                </div>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-xs text-gray-700">
                  ✍️ Users will be able to draw their signature with mouse/touch on a canvas of this size.
                </p>
              </div>
            </div>
          )}

          {/* Image options */}
          {editedField.type === 'image' && (
            <div className="space-y-4 p-4 bg-green-50/50 border border-green-100 rounded-xl">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Photo Capture Options</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Images
                </label>
                <input
                  type="number"
                  value={editedField.maxImages || 10}
                  onChange={(e) => setEditedField({ ...editedField, maxImages: Number(e.target.value) } as FieldSchema)}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all bg-white"
                  min="1"
                  max="50"
                />
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-xs text-gray-700">
                  📷 Users can capture multiple photos with notes for each image.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gradient-to-r from-gray-50/50 to-white border-t border-gray-200/50 px-6 py-4 flex items-center justify-between z-10 rounded-b-2xl">
          <div>
            {onDelete && (
              <button
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                className="px-5 py-2.5 text-red-600 hover:bg-red-50 rounded-xl font-medium transition-all duration-200 border border-red-200 hover:border-red-300 shadow-sm hover:shadow-md"
              >
                Delete Field
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editedField.label.trim()}
              className={`px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg ${
                editedField.label.trim()
                  ? 'text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl hover:scale-105'
                  : 'text-gray-400 bg-gray-200 cursor-not-allowed'
              }`}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
